<template>
    <div
        class="process-definition-placeholder"
        @dragover.prevent
        @dragenter="(e) => toggleDrag(true, e)"
    >
        <div>
            <h2>This process does not have a diagram yet.</h2>
            <div v-if="!roles?.length">
                <p>
                    You need to have at least one role in the domain to model a
                    process.
                </p>
                <div class="process-definition-buttons">
                    <Button
                        primary
                        label="Add Role"
                        icon="pi pi-plus"
                        @click="() => openEditRole()"
                    />
                    <Button
                        class="p-button-secondary"
                        label="Go to domain"
                        icon="pi pi-cloud"
                        as="RouterLink"
                        :to="{
                            name: 'domainPage',
                            params: {
                                orgName: organization?.name,
                                stekName: stek?.name,
                                domainId: domain?.identity,
                            },
                        }"
                    />
                </div>
            </div>
            <div v-else class="process-definition-buttons">
                <Button
                    primary
                    label="Generate first draft"
                    icon="pi pi-sparkles"
                    v-tooltip.bottom="
                        'Describe your process and we\'ll generate the first draft for you!'
                    "
                    @click="openGenerateDialog"
                />
                <Button
                    class="p-button-secondary"
                    label="Create a diagram"
                    v-tooltip.bottom="'Start from scratch'"
                    @click="createProcessDefinition"
                />
                <Button
                    class="p-button-secondary"
                    label="Upload a BPMN"
                    v-tooltip.bottom="
                        'Already have an existing diagram? Upload it here.'
                    "
                    @click="fileUploadElement?.click()"
                />
                <input
                    name="file"
                    type="file"
                    accept=".bpmn"
                    style="display: none"
                    ref="fileUploadElement"
                    @change="
                        (e) =>
                            uploadProcessDefinition(
                                (e as HTMLInputEvent).target?.files
                            )
                    "
                />
            </div>
        </div>
        <div
            v-if="isDragging"
            class="dropzone"
            @dragover.prevent
            @drop.prevent="
                (e) => uploadProcessDefinition(e.dataTransfer?.files)
            "
            @dragleave="(e) => toggleDrag(false, e)"
        >
            <h2>Drop your BPMN to upload</h2>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { HTMLInputEvent } from "@/types";
import {
    useRoute,
    useRouter,
    isNavigationFailure,
    NavigationFailureType,
} from "vue-router";
import { useEditStore } from "@/composables/edit-manager";
import { initBpmn } from "@/composables/bpmn-manager/helpers";
import { BpmnArtefact } from "@/lib/artefacts";
import { useRolesManager } from "@/stores/roles-store";
import { useGenerateBpmnDialog } from "@/composables/generate-bpmn-dialog";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useCurrentEntityManager } from "@/stores/entity-store";

const model = defineModel<BpmnArtefact>({ required: true });

const route = useRoute();
const router = useRouter();
const { roles } = useRolesManager();
const { organization, stek, domain } = useCurrentEntityManager();
const fileUploadElement = ref<HTMLElement>();

const { startEditing } = useEditStore();
const { open: openEditRole } = useEditRoleDialog();
const { open: openGenerateDialog } = useGenerateBpmnDialog({
    roles,
    artefact: model,
});
const isDragging = ref(false);

/**
 * Check if the given filelist contains exactly one BPMN file.
 */
const isBpmnFile = (files?: FileList | null) => {
    if (!files || files.length !== 1) {
        return false;
    }
    return files[0].name.endsWith(".bpmn");
};

const toggleDrag = (toggle: boolean, event: DragEvent) => {
    event.preventDefault();
    isDragging.value = toggle;
};

const createProcessDefinition = async () => {
    const requiredLanes = (roles.value || []).map((role) => ({
        name: role.name,
        roleId: role.identity,
    }));
    const xml = await initBpmn(requiredLanes);
    if (!xml) {
        throw Error("Could not generate base BPMN.");
    }
    await model.value.updateContent(xml);

    await navigateToDefinition();
};

const uploadProcessDefinition = async (files?: FileList | null) => {
    if (!files || !isBpmnFile(files)) {
        return;
    }
    await navigateToDefinition();
    await model.value.updateContent(files[0], true);
};

/**
 * When the button is clicked to create a BPMN, forward the user to
 * the process definition page so the user can immediately start
 * editing.
 */
const navigateToDefinition = async () => {
    const failure = await router.push({
        name: "processDefinitionPage",
        params: route.params,
    });
    // Do not navigate the user if the navigation was aborted - this could
    // happen when the user was also editing something on the page and clicked
    // 'continue editing' after a confirmation popup which was triggered
    // through a router guard.
    if (isNavigationFailure(failure, NavigationFailureType.aborted)) {
        return;
    }

    startEditing();
};
</script>

<style scoped lang="scss">
.process-definition-placeholder {
    border-radius: 5px;
    min-height: 300px;
    height: 100%;
    background: var(--palette-grey-100);
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;

    p {
        margin-bottom: 1rem;
    }

    .dropzone {
        z-index: 1;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex: 1;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0.75);
        outline: 2px dashed var(--stekz-primary-color);

        * {
            pointer-events: none;
        }
    }
}

.process-definition-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
}
</style>
